import { atom, type Atom, type WritableAtom } from "jotai";
import { jotaiStore } from "@/components/providers/jotai-provider";
import { atomEffect, withAtomEffect } from "jotai-effect";

import { withTimeout } from "./withTimeout";
import { ConvexClient } from "convex/browser";
import { convexClient } from "@/app/ConvexClientProvider";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { PARTICIPANT_PROPERTY_FIELDS, type ParticipantPropertyName } from "@convex/shared";
import {
  notifyParticipantAttached,
  notifyParticipantDetached,
  type ParticipantContext,
} from "./participantLifecycleHooks";

export {
  registerParticipantLifecycleHooks,
  type ParticipantContext,
  type ParticipantLifecycleHooks,
} from "./participantLifecycleHooks";

// Separate ConvexClient for subscriptions (ConvexReactClient doesn't have onUpdate)
// Auth is set via setSubscriptionClientAuth() when ConvexProviderWithClerk mounts
const convexUrl = typeof window !== "undefined" ? process.env.NEXT_PUBLIC_CONVEX_URL : undefined;
let subscriptionClient: ConvexClient | null = null;
let authenticatedSubject: string | null | undefined;
const authenticatedSubjectResolvers: Array<{
  resolve(subject: string | null | undefined): void;
  timeout: ReturnType<typeof setTimeout>;
}> = [];

export function getSubscriptionClient() {
  if (!subscriptionClient && convexUrl) {
    // This client carries subscriptions and automatic frontier writes only, never user-authored mutations.
    subscriptionClient = new ConvexClient(convexUrl, { unsavedChangesWarning: false });
  }
  return subscriptionClient;
}

// Called by ConvexClientProvider to set auth on the subscription client
export function initSubscriptionClientAuth(fetchToken: () => Promise<string | null>) {
  const client = getSubscriptionClient();
  if (client) {
    client.setAuth(fetchToken);
  }
}

export function setAuthenticatedSubject(subject: string | null | undefined): void {
  authenticatedSubject = subject;
  if (subject === undefined) return;
  for (const resolver of authenticatedSubjectResolvers.splice(0)) {
    clearTimeout(resolver.timeout);
    resolver.resolve(subject);
  }
}

export function getAuthenticatedSubject(): string | null | undefined {
  return authenticatedSubject;
}

export function waitForAuthenticatedSubject(timeoutMs: number): Promise<string | null | undefined> {
  if (authenticatedSubject !== undefined) return Promise.resolve(authenticatedSubject);
  return new Promise((resolve) => {
    const timeout = setTimeout(() => {
      const index = authenticatedSubjectResolvers.findIndex(
        (resolver) => resolver.resolve === resolve,
      );
      if (index !== -1) authenticatedSubjectResolvers.splice(index, 1);
      resolve(undefined);
    }, timeoutMs);
    authenticatedSubjectResolvers.push({ resolve, timeout });
  });
}

export type ParticipantPropertyAtoms<T> = readonly [
  WritableAtom<T | undefined, [T | undefined], void>,
  WritableAtom<Record<string, T | undefined>, [Record<string, T | undefined>], void>,
  Atom<null>, // Stubbed out - will be replaced with Convex query for previous session
  Atom<boolean>, // Whether the initial subscription value has been received
];

type Options = {
  debounce?: boolean | number;
};

export type ParticipantRegistrationResult = {
  participantId: Id<"sessionParticipants"> | null;
  isFirstJoin: boolean;
};

const participantContextAtom = atom<ParticipantContext | null>(null);

export type ParticipantPropertySyncStatus = {
  state: "idle" | "pending" | "saved" | "error";
  lastSuccessfulSync?: number;
  retryAttempt?: number;
};

export const participantPropertySyncStatusAtom = atom<
  Partial<Record<ParticipantPropertyName, ParticipantPropertySyncStatus>>
>({});

const registry = new Map<string, RegistryEntry<any>>();

let latestEnsureContext: ParticipantContext | null = null;
let ensureRequestVersion = 0;
let ensureRetryAttempt = 0;
let ensureInFlight = false;
let ensureRetryTimeout: ReturnType<typeof setTimeout> | null = null;
let ensureResultResolver: ((result: ParticipantRegistrationResult | null) => void) | null = null;

function resolveEnsureResult(result: ParticipantRegistrationResult | null) {
  ensureResultResolver?.(result);
  ensureResultResolver = null;
}

function clearEnsureRetry() {
  if (ensureRetryTimeout) {
    clearTimeout(ensureRetryTimeout);
    ensureRetryTimeout = null;
  }
}

async function runEnsureParticipant() {
  const params = latestEnsureContext;
  if (!params || ensureInFlight) return;

  ensureInFlight = true;
  const requestVersion = ensureRequestVersion;

  try {
    const registration = await getConvexClient().mutation(api.sessions.ensureParticipant, {
      sessionId: params.sessionId,
      participantKey: params.participantKey ?? undefined,
      anonUserId: params.anonUserId ?? undefined,
      timezone: params.timezone ?? undefined,
      requireAuth: requiresAuthentication(params),
    });

    if (requestVersion === ensureRequestVersion) {
      ensureRetryAttempt = 0;
      registry.forEach((entry) => entry.retryPending());
      resolveEnsureResult(registration);
    }
  } catch (error: unknown) {
    if (requestVersion === ensureRequestVersion && latestEnsureContext) {
      ensureRetryAttempt += 1;
      console.error("Failed to ensure participant", error);
      const retryDelay = Math.min(1000 * 2 ** (ensureRetryAttempt - 1), 15_000);
      ensureRetryTimeout = setTimeout(() => {
        ensureRetryTimeout = null;
        void runEnsureParticipant();
      }, retryDelay);
    }
  } finally {
    ensureInFlight = false;
    if (requestVersion !== ensureRequestVersion && latestEnsureContext) {
      void runEnsureParticipant();
    }
  }
}

function ensureParticipantWithRetry(
  params: ParticipantContext,
): Promise<ParticipantRegistrationResult | null> {
  resolveEnsureResult(null);
  latestEnsureContext = params;
  ensureRequestVersion += 1;
  ensureRetryAttempt = 0;
  clearEnsureRetry();
  const result = new Promise<ParticipantRegistrationResult | null>((resolve) => {
    ensureResultResolver = resolve;
  });
  void runEnsureParticipant();
  return result;
}

function cancelEnsureParticipantRetry() {
  latestEnsureContext = null;
  ensureRequestVersion += 1;
  ensureRetryAttempt = 0;
  clearEnsureRetry();
  resolveEnsureResult(null);
}

/**
 * Get the key to use for looking up this participant's data.
 * Priority: Clerk ID > Anonymous UUID > Jitsi ID
 */
function getLookupKey(ctx: ParticipantContext): string {
  // Clerk user ID (starts with "user_")
  if (ctx.userId && ctx.userId.startsWith("user_")) {
    return ctx.userId;
  }
  // Anonymous user ID from localStorage (starts with "anon_")
  if (ctx.anonUserId && ctx.anonUserId.startsWith("anon_")) {
    return ctx.anonUserId; // Stable across refreshes!
  }
  // Fallback to Jitsi ID (changes on refresh)
  return ctx.participantKey ?? "";
}

function requiresAuthentication(ctx: ParticipantContext): boolean {
  return Boolean(ctx.userId?.startsWith("user_"));
}

/**
 * Get the shared ConvexReactClient singleton.
 * This client is authenticated via ConvexProviderWithClerk once React mounts.
 */
function getConvexClient() {
  if (!convexClient) {
    throw new Error("Convex client not initialized - NEXT_PUBLIC_CONVEX_URL may not be set");
  }
  return convexClient;
}

function propertyNameFromInput(name: string): ParticipantPropertyName {
  if (name in PARTICIPANT_PROPERTY_FIELDS) {
    return name as ParticipantPropertyName;
  }
  throw new Error(`Unsupported property ${name}`);
}

function valuesEqual(a: unknown, b: unknown) {
  if (a === b) return true;
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
}

type RegistryEntry<T> = {
  name: ParticipantPropertyName;
  baseLocalAtom: WritableAtom<T | undefined, [T | undefined], void>;
  localAtom: WritableAtom<T | undefined, [T | undefined], void>;
  allAtom: WritableAtom<Record<string, T | undefined>, [Record<string, T | undefined>], void>;
  hydratedAtom: Atom<boolean>;
  remoteStateBaseAtom: WritableAtom<
    Record<string, T | undefined>,
    [
      | Record<string, T | undefined>
      | ((prev: Record<string, T | undefined>) => Record<string, T | undefined>),
    ],
    void
  >;
  previousSessionAtom: Atom<null>;
  retryPending: () => void;
  flushPending: (ctx: ParticipantContext) => void;
  settlePending: (ctx: ParticipantContext) => Promise<void>;
  cancelFlushRetries: () => void;
  invalidateConfirmedValues: () => void;
  resetSync: () => void;
};

const PARTICIPANT_LIFECYCLE_SETTLEMENT_TIMEOUT_MS = 5_000;

export type ParticipantLifecycleHandle = {
  readonly context: ParticipantContext;
  readonly registration: Promise<ParticipantRegistrationResult | null>;
  isCurrent(): boolean;
  updateParticipantKey(participantKey: string): Promise<ParticipantRegistrationResult | null>;
  clear(): Promise<void>;
};

type ParticipantLifecycle = ParticipantLifecycleHandle & {
  abandon(): void;
  clearLegacy(): void;
};

type AcquireParticipantLifecycleOptions = {
  isCancelled?: () => boolean;
};

let activeParticipantLifecycle: ParticipantLifecycle | null = null;
let legacyParticipantLifecycle: ParticipantLifecycle | null = null;
let participantLifecycleRequest = 0;
const participantLifecycleBarriers = new Map<string, Promise<void>>();

async function syncToConvex<T>(
  ctx: ParticipantContext,
  name: ParticipantPropertyName,
  value: T | undefined,
  options?: { enforceExpectedValue: boolean; expectedValue: T | undefined },
) {
  return await getConvexClient().mutation(api.sessions.setParticipantProperty, {
    sessionId: ctx.sessionId,
    property: name,
    value: value ?? null,
    participantKey: ctx.participantKey ?? undefined,
    anonUserId: ctx.anonUserId ?? undefined,
    requireAuth: requiresAuthentication(ctx),
    enforceExpectedValue: options?.enforceExpectedValue,
    expectedValue: options?.enforceExpectedValue ? options.expectedValue ?? null : undefined,
  });
}

async function loadParticipantPropertyValue<T>(
  ctx: ParticipantContext,
  name: ParticipantPropertyName,
) {
  const values = await getConvexClient().query(api.sessions.participantPropertyMap, {
    sessionId: ctx.sessionId,
    property: name,
  });
  return values[getLookupKey(ctx)] as T | undefined;
}

function installParticipantContext(params: ParticipantContext): void {
  const previous = jotaiStore.get(participantContextAtom);
  const previousSignature = previous ? `${previous.sessionId}:${getLookupKey(previous)}` : null;
  const nextSignature = `${params.sessionId}:${getLookupKey(params)}`;
  registry.forEach((entry) => entry.cancelFlushRetries());
  if (!previous) {
    registry.forEach((entry) => entry.invalidateConfirmedValues());
  }
  if (previousSignature && previousSignature !== nextSignature) {
    registry.forEach((entry) => entry.resetSync());
  }

  jotaiStore.set(participantContextAtom, params);
}

function createParticipantLifecycleHandle(
  initialContext: ParticipantContext,
): ParticipantLifecycle {
  let context = initialContext;
  let registration = ensureParticipantWithRetry(context);
  let cleanupPromise: Promise<void> | null = null;
  let handle: ParticipantLifecycle;

  const clearGlobalState = (): void => {
    if (activeParticipantLifecycle !== handle) return;
    activeParticipantLifecycle = null;
    if (legacyParticipantLifecycle === handle) legacyParticipantLifecycle = null;
    cancelEnsureParticipantRetry();
    jotaiStore.set(participantContextAtom, null);
    registry.forEach((entry) => entry.resetSync());
  };

  const startOwnServerCleanup = (): Promise<void> => {
    if (cleanupPromise) return cleanupPromise;

    const capturedContext = context;
    const settlements = Array.from(registry.values(), (entry) =>
      entry.settlePending(capturedContext),
    );
    settlements.push(
      ...notifyParticipantDetached(capturedContext).map((settlement) =>
        Promise.resolve(settlement),
      ),
    );
    if (capturedContext.participantKey) {
      const participantKey = capturedContext.participantKey;
      settlements.push(
        Promise.resolve().then(async () => {
          await getConvexClient().mutation(api.sessions.clearParticipantKey, {
            sessionId: capturedContext.sessionId,
            participantKey,
          });
        }),
      );
    }

    cleanupPromise = withTimeout(
      () => Promise.allSettled(settlements).then(() => undefined),
      PARTICIPANT_LIFECYCLE_SETTLEMENT_TIMEOUT_MS,
      undefined,
    );
    const sessionKey = String(capturedContext.sessionId);
    participantLifecycleBarriers.set(sessionKey, cleanupPromise);
    void cleanupPromise.finally(() => {
      if (participantLifecycleBarriers.get(sessionKey) === cleanupPromise) {
        participantLifecycleBarriers.delete(sessionKey);
      }
    });
    return cleanupPromise;
  };

  handle = {
    get context() {
      return context;
    },
    get registration() {
      return registration;
    },
    isCurrent: () => activeParticipantLifecycle === handle,
    updateParticipantKey: (participantKey) => {
      if (context.participantKey === participantKey) {
        return Promise.resolve(null);
      }
      context = { ...context, participantKey };
      // A superseded handle still learns its departing session's key for its
      // own server clear, but it must not enrich the live global registry.
      if (activeParticipantLifecycle !== handle) return Promise.resolve(null);
      installParticipantContext(context);
      registration = ensureParticipantWithRetry(context);
      return registration;
    },
    clear: () => {
      const settlement = startOwnServerCleanup();
      clearGlobalState();
      return settlement;
    },
    abandon: () => clearGlobalState(),
    clearLegacy: () => {
      if (activeParticipantLifecycle !== handle) return;
      const capturedContext = context;
      registry.forEach((entry) => entry.flushPending(capturedContext));
      notifyParticipantDetached(capturedContext);
      clearGlobalState();
      if (!capturedContext.participantKey) return;
      getConvexClient()
        .mutation(api.sessions.clearParticipantKey, {
          sessionId: capturedContext.sessionId,
          participantKey: capturedContext.participantKey,
        })
        .catch((error: unknown) => {
          console.error("Failed to clear participant key", error);
        });
    },
  };

  activeParticipantLifecycle = handle;
  installParticipantContext(context);
  notifyParticipantAttached(context);
  return handle;
}

/**
 * Acquires the process-global participant-property registry for one call.
 * Supersession starts the departing lease's captured cleanup before the new
 * context is installed. A same-session successor additionally waits for that
 * bounded cleanup so an old participant-key clear cannot race its enrichment.
 */
export async function acquireParticipantLifecycle(
  params: ParticipantContext,
  options: AcquireParticipantLifecycleOptions = {},
): Promise<ParticipantLifecycleHandle | null> {
  const request = ++participantLifecycleRequest;
  if (options.isCancelled?.()) return null;

  const predecessor = activeParticipantLifecycle;
  predecessor?.clear();
  const predecessorBarrier = participantLifecycleBarriers.get(String(params.sessionId));
  if (predecessorBarrier) await predecessorBarrier;

  // The operation token and acquisition generation are checked after the only
  // predecessor await and before installing any new global state. This is the
  // lease precondition relied on by SessionSupervisor.close().
  if (options.isCancelled?.() || request !== participantLifecycleRequest) return null;

  const intervening = activeParticipantLifecycle;
  if (intervening) {
    intervening.clear();
    const interveningBarrier = participantLifecycleBarriers.get(String(params.sessionId));
    if (interveningBarrier) await interveningBarrier;
    if (options.isCancelled?.() || request !== participantLifecycleRequest) return null;
  }

  return createParticipantLifecycleHandle(params);
}

export function setActiveParticipant(
  params: ParticipantContext,
): Promise<ParticipantRegistrationResult | null> {
  if (typeof window === "undefined") return Promise.resolve(null);
  if (!convexClient) {
    console.warn("Convex client not initialized; participant properties disabled.");
    return Promise.resolve(null);
  }
  participantLifecycleRequest += 1;
  activeParticipantLifecycle?.abandon();
  legacyParticipantLifecycle = createParticipantLifecycleHandle(params);
  return legacyParticipantLifecycle.registration;
}

export function createActiveParticipantLifecycleAtom(
  createContext: () => ParticipantContext,
  options?: { enabled?: boolean },
) {
  return atomEffect(() => {
    if (options?.enabled === false) return;
    void setActiveParticipant(createContext());
    return clearActiveParticipant;
  });
}

export function updateParticipantPropertyParticipantKey(
  sessionId: Id<"sessions">,
  participantKey: string,
): Promise<ParticipantRegistrationResult | null> {
  if (typeof window === "undefined" || !convexClient) return Promise.resolve(null);

  const lifecycle = activeParticipantLifecycle;
  if (!lifecycle || lifecycle.context.sessionId !== sessionId) {
    return Promise.resolve(null);
  }
  return lifecycle.updateParticipantKey(participantKey);
}

export function clearActiveParticipant() {
  if (typeof window === "undefined" || !convexClient) return;
  participantLifecycleRequest += 1;
  if (legacyParticipantLifecycle) {
    legacyParticipantLifecycle.clearLegacy();
    return;
  }
  void activeParticipantLifecycle?.clear();
}

export function createParticipantPropertyAtom<T>(
  propertyName: ParticipantPropertyName,
  options?: Options,
): ParticipantPropertyAtoms<T> {
  const name = propertyNameFromInput(propertyName);
  const existing = registry.get(name);
  if (existing) {
    return [
      existing.localAtom,
      existing.allAtom,
      existing.previousSessionAtom,
      existing.hydratedAtom,
    ];
  }

  // Simple in-memory atom for optimistic updates
  const baseLocalAtom: WritableAtom<T | undefined, [T | undefined], void> = atom<T | undefined>(
    undefined,
  );
  // Stubbed out - will be replaced with Convex query for previous session data
  const previousSessionAtom: Atom<null> = atom(() => null);

  // Track pending local updates - don't let subscription overwrite while pending.
  // Use an id (not boolean) to avoid races with multiple in-flight syncs.
  let updateCounter = 0;
  let pendingUpdateId = 0;

  const debounceMs = options?.debounce
    ? typeof options.debounce === "number"
      ? options.debounce
      : 300
    : 0;

  const remoteStateBaseAtom = atom<Record<string, T | undefined>>({});
  const hydrationBaseAtom = atom(false);

  let debounceTimeout: ReturnType<typeof setTimeout> | null = null;
  let retryTimeout: ReturnType<typeof setTimeout> | null = null;
  let retryAttempt = 0;
  const inFlightUpdateIds = new Set<number>();
  const flushRetryTimeouts = new Set<ReturnType<typeof setTimeout>>();
  let flushGeneration = 0;
  let subscriptionGeneration = 0;
  const confirmedRemoteValues = new Map<string, T | undefined>();
  // The save in flight, and the last acknowledgement whose canonical value has not yet
  // been echoed by the subscription. A newer local edit or a reset clears both.
  let inFlightSave: { key: string; updateId: number } | null = null;
  let lastAck: {
    key: string;
    lookupKey: string;
    submitted: T | undefined;
    canonical: T | undefined;
  } | null = null;
  let freshReadVersion = 0;
  let observedDuringSend: Array<T | undefined> = [];

  const confirmedValueKey = (ctx: ParticipantContext) => `${ctx.sessionId}:${getLookupKey(ctx)}`;
  const invalidateConfirmedValues = () => confirmedRemoteValues.clear();

  const setSyncStatus = (status: ParticipantPropertySyncStatus) => {
    jotaiStore.set(participantPropertySyncStatusAtom, (previous) => ({
      ...previous,
      [name]: { ...previous[name], ...status },
    }));
  };

  const clearScheduledSyncs = () => {
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
      debounceTimeout = null;
    }
    if (retryTimeout) {
      clearTimeout(retryTimeout);
      retryTimeout = null;
    }
  };

  // Take the server's stored value as both the confirmed baseline and the local value.
  const adopt = (key: string, lookupKey: string, value: T | undefined) => {
    confirmedRemoteValues.set(key, value);
    jotaiStore.set(baseLocalAtom, value);
    jotaiStore.set(remoteStateBaseAtom, (previous) => ({ ...previous, [lookupKey]: value }));
  };

  // A subscription value that is neither the submitted nor the canonical value could be
  // older (a late echo) or newer (another device); only a fresh read can tell.
  const readFreshAfterAck = async (ack: NonNullable<typeof lastAck>) => {
    const ctx = jotaiStore.get(participantContextAtom);
    if (!ctx || confirmedValueKey(ctx) !== ack.key) return;
    const readVersion = ++freshReadVersion;
    try {
      const fresh = await loadParticipantPropertyValue<T>(ctx, name);
      if (lastAck === ack && freshReadVersion === readVersion) {
        lastAck = null;
        adopt(ack.key, ack.lookupKey, fresh);
      }
    } catch (error: unknown) {
      console.error(`[convex-properties] failed to refresh ${name} after save`, error);
    }
  };

  const runSync = async (updateId: number) => {
    if (pendingUpdateId !== updateId || inFlightUpdateIds.has(updateId)) return;
    inFlightUpdateIds.add(updateId);

    const ctx = jotaiStore.get(participantContextAtom);
    try {
      if (!ctx || !getLookupKey(ctx)) {
        throw new Error("Participant persistence is not ready");
      }

      // While pendingUpdateId === updateId, baseLocalAtom is exactly this
      // update's value — every other writer zeroes pendingUpdateId first.
      const value = jotaiStore.get(baseLocalAtom);
      const key = confirmedValueKey(ctx);
      const lookupKey = getLookupKey(ctx);
      inFlightSave = { key, updateId };
      observedDuringSend = [];
      const result = await syncToConvex(ctx, name, value);
      if (pendingUpdateId !== updateId) return;

      // Treat the legacy void response as success during a rolling deploy.
      if (result?.updated !== false) {
        // A save result that carries `value` is telling us what the server stored (it may
        // normalise what we sent); a result without one leaves the submitted value as the
        // baseline, which is what old servers and other properties do.
        const stored: T | undefined =
          result && "value" in result ? (result.value as T | undefined) : value;
        confirmedRemoteValues.set(key, stored);
        if (result && "value" in result) {
          lastAck = { key, lookupKey, submitted: value, canonical: stored };
          if (!valuesEqual(stored, value)) adopt(key, lookupKey, stored);
        } else {
          lastAck = null;
        }
      }

      pendingUpdateId = 0;
      inFlightSave = null;
      const ack = lastAck;
      if (
        ack &&
        observedDuringSend.some(
          (seen) => !valuesEqual(seen, ack.submitted) && !valuesEqual(seen, ack.canonical),
        )
      ) {
        void readFreshAfterAck(ack);
      }
      observedDuringSend = [];
      retryAttempt = 0;
      setSyncStatus({
        state: "saved",
        lastSuccessfulSync: Date.now(),
        retryAttempt: 0,
      });
    } catch (error: unknown) {
      if (pendingUpdateId !== updateId) return;

      retryAttempt += 1;
      console.error(`[convex-properties] failed to sync ${name}`, error);
      setSyncStatus({
        state: "error",
        retryAttempt,
      });

      const retryDelay = Math.min(1000 * 2 ** (retryAttempt - 1), 15_000);
      retryTimeout = setTimeout(() => {
        retryTimeout = null;
        void runSync(updateId);
      }, retryDelay);
    } finally {
      inFlightUpdateIds.delete(updateId);
    }
  };

  const retryPending = () => {
    if (pendingUpdateId === 0) return;
    clearScheduledSyncs();
    setSyncStatus({ state: "pending" });
    void runSync(pendingUpdateId);
  };

  const cancelFlushRetries = () => {
    flushGeneration += 1;
    for (const timeout of flushRetryTimeouts) {
      clearTimeout(timeout);
    }
    flushRetryTimeouts.clear();
  };

  // Best-effort save of an unsent edit as the participant detaches. Captures the
  // context and value locally because resetSync() wipes the regular retry state
  // right after this runs, so the flush must survive on its own. It retries with
  // backoff, and doesn't coordinate with an in-flight runSync for the same edit:
  // that attempt dies silently once pendingUpdateId resets, and a duplicate
  // write of the identical value is idempotent.
  const flushPending = (ctx: ParticipantContext) => {
    if (pendingUpdateId === 0) return;
    clearScheduledSyncs();
    cancelFlushRetries();

    const value = jotaiStore.get(baseLocalAtom);
    const valueKey = confirmedValueKey(ctx);
    let expectedValue = confirmedRemoteValues.get(valueKey);
    // Map.has distinguishes "not loaded yet" from a confirmed empty value.
    let expectedValueKnown = confirmedRemoteValues.has(valueKey);
    const counterAtFlush = updateCounter;
    const generationAtFlush = flushGeneration;
    const stillOwnsFlush = () =>
      flushGeneration === generationAtFlush && updateCounter === counterAtFlush;
    const attempt = async (attemptNumber: number) => {
      // Re-attachment, an accepted remote value, or a newer local edit owns the
      // server value now — abort rather than clobber it with this stale flush.
      if (!stillOwnsFlush()) return;

      try {
        if (!expectedValueKnown) {
          const loadedValue = await loadParticipantPropertyValue<T>(ctx, name);
          if (!stillOwnsFlush()) return;
          expectedValue = loadedValue;
          expectedValueKnown = true;
          confirmedRemoteValues.set(valueKey, loadedValue);
        }

        const result = await syncToConvex(ctx, name, value, {
          enforceExpectedValue: true,
          expectedValue,
        });
        if (!stillOwnsFlush()) return;

        if (result?.updated === false) {
          cancelFlushRetries();
          return;
        }
        confirmedRemoteValues.set(valueKey, value);
      } catch (error: unknown) {
        if (!stillOwnsFlush()) return;
        if (attemptNumber < 4) {
          const timeout = setTimeout(() => {
            flushRetryTimeouts.delete(timeout);
            void attempt(attemptNumber + 1);
          }, 2000 * attemptNumber);
          flushRetryTimeouts.add(timeout);
        } else {
          console.error(`[convex-properties] final ${name} flush failed`, error);
        }
      }
    };
    void attempt(1);
  };

  // Lease cleanup owns this snapshot independently of the process-global
  // registry. A successor may reset or edit the same local atom while the
  // departing write is in flight; those actions must not cancel this session's
  // captured server cleanup.
  const settlePending = (ctx: ParticipantContext): Promise<void> => {
    if (pendingUpdateId === 0) return Promise.resolve();
    clearScheduledSyncs();

    const value = jotaiStore.get(baseLocalAtom);
    return new Promise((resolve) => {
      const attempt = async (attemptNumber: number): Promise<void> => {
        try {
          await syncToConvex(ctx, name, value);
          resolve();
        } catch (error: unknown) {
          if (attemptNumber < 4) {
            setTimeout(() => void attempt(attemptNumber + 1), 2000 * attemptNumber);
            return;
          }
          console.error(`[convex-properties] final ${name} lease flush failed`, error);
          resolve();
        }
      };
      void attempt(1);
    });
  };

  const resetSync = () => {
    clearScheduledSyncs();
    pendingUpdateId = 0;
    inFlightSave = null;
    lastAck = null;
    observedDuringSend = [];
    retryAttempt = 0;
    invalidateConfirmedValues();
    jotaiStore.set(remoteStateBaseAtom, {});
    jotaiStore.set(baseLocalAtom, undefined);
    jotaiStore.set(hydrationBaseAtom, false);
    jotaiStore.set(participantPropertySyncStatusAtom, (previous) => ({
      ...previous,
      [name]: { state: "idle" },
    }));
  };

  const remoteStateAtom = withAtomEffect(remoteStateBaseAtom, (get, set) => {
    if (typeof window === "undefined" || !convexClient) {
      return;
    }

    // Local-state resets on context changes happen in resetSync(), driven by
    // attach/detach — this effect only manages the remote subscription.
    const ctx = get(participantContextAtom);
    if (!ctx) return;

    let unsub: (() => void) | undefined;
    try {
      const client = getSubscriptionClient();
      if (!client) {
        return;
      }
      // ConvexClient.onUpdate replays an in-memory result when the same query is
      // resubscribed before the server acknowledges the previous unsubscribe.
      // Give each attachment a distinct query token so hydration only follows a
      // server result requested for this attachment, not that cached replay.
      subscriptionGeneration += 1;
      unsub = client.onUpdate(
        api.sessions.participantPropertyMap,
        {
          sessionId: ctx.sessionId,
          property: name,
          subscriptionGeneration,
        },
        (record) => {
          const activeCtx = get(participantContextAtom);
          if (!activeCtx || activeCtx.sessionId !== ctx.sessionId) {
            return;
          }
          const data = (record ?? {}) as Record<string, T | undefined>;
          set(remoteStateBaseAtom, data);
          const lookupKey = getLookupKey(activeCtx);
          const localValue = data[lookupKey];
          const key = confirmedValueKey(activeCtx);
          if (inFlightSave?.key === key && pendingUpdateId === inFlightSave.updateId) {
            observedDuringSend.push(localValue);
          }
          if (pendingUpdateId === 0 && lastAck?.key === key) {
            const ack = lastAck;
            if (valuesEqual(localValue, ack.submitted) || valuesEqual(localValue, ack.canonical)) {
              // Our own echo: the canonical value is confirmed and the ack is settled.
              lastAck = null;
              adopt(key, ack.lookupKey, ack.canonical);
            } else {
              void readFreshAfterAck(ack);
            }
            set(hydrationBaseAtom, true);
            return;
          }
          confirmedRemoteValues.set(key, localValue);
          const currentValue = get(baseLocalAtom);
          if (
            pendingUpdateId === 0 &&
            localValue !== undefined &&
            !valuesEqual(localValue, currentValue)
          ) {
            cancelFlushRetries();
            set(baseLocalAtom, localValue);
          }
          set(hydrationBaseAtom, true);
        },
        (error: unknown) => {
          console.error(`[convex-properties] subscription error for ${name}`, error);
        },
      );
    } catch (error: unknown) {
      console.error("[convex-properties] Failed to subscribe to participant property", error);
    }

    return () => {
      unsub?.();
    };
  });

  const allAtom = atom(
    (get) => get(remoteStateAtom),
    (_get, set, value: Record<string, T | undefined>) => set(remoteStateBaseAtom, value),
  );
  const hydratedAtom = atom((get) => {
    // Ensure the subscription effect is active when hydration is observed directly.
    get(remoteStateAtom);
    return get(hydrationBaseAtom);
  });

  const localAtom: WritableAtom<T | undefined, [T | undefined], void> = atom(
    (get) => {
      // Ensure the subscription effect is active whenever this property is used.
      get(remoteStateAtom);
      return get(baseLocalAtom);
    },
    (get, set, update: T | undefined | ((prev: T | undefined) => T | undefined)) => {
      const prev = get(baseLocalAtom);
      const next =
        typeof update === "function"
          ? (update as (value: T | undefined) => T | undefined)(prev)
          : update;
      cancelFlushRetries();
      set(baseLocalAtom, next);
      const updateId = ++updateCounter;
      lastAck = null;
      pendingUpdateId = updateId;
      retryAttempt = 0;
      setSyncStatus({ state: "pending" });

      const ctxSnapshot = get(participantContextAtom);
      const lookupKey = ctxSnapshot ? getLookupKey(ctxSnapshot) : null;
      if (ctxSnapshot && lookupKey) {
        set(remoteStateBaseAtom, (previous) => {
          const merged = { ...previous };
          if (next === undefined || next === null) {
            delete merged[lookupKey];
          } else {
            merged[lookupKey] = next;
          }
          return merged;
        });
      }

      clearScheduledSyncs();

      const scheduleSync = () => void runSync(updateId);

      if (debounceMs > 0) {
        debounceTimeout = setTimeout(scheduleSync, debounceMs);
      } else {
        scheduleSync();
      }
    },
  );

  const entry: RegistryEntry<T> = {
    name,
    baseLocalAtom,
    localAtom,
    allAtom,
    hydratedAtom,
    remoteStateBaseAtom,
    previousSessionAtom,
    retryPending,
    flushPending,
    settlePending,
    cancelFlushRetries,
    invalidateConfirmedValues,
    resetSync,
  };

  registry.set(name, entry);

  return [entry.localAtom, entry.allAtom, entry.previousSessionAtom, entry.hydratedAtom];
}

export function retryParticipantPropertySync(name: ParticipantPropertyName) {
  registry.get(name)?.retryPending();
}
